import {
  ICompanyDetail,
  ICompanyRow,
  ICrmListResponse,
  IOrganizationDetail,
  IOrganizationRow,
  IPersonDetail,
  IDealRow,
  IPersonRow,
  roleSet,
  roleSetToLabel,
} from '@common/interfaces/crm'
import { IMentionRow } from '@common/interfaces/mention'
import {
  IOrganizationOverviewFeed,
  IOverviewFeed,
  IPersonOverviewFeed,
} from '@common/interfaces/overview'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { createLoadingDataReducer } from '../../helpers/redux'
import { ILoadingData } from '../types'
import * as actions from './actions'

// Relationship rails (People / Related Companies / Referrals / …) keyed by
// `${kind}:${ownerId}` so every rail on a page keeps its own list state. Server
// returns { data, total }; loadMore appends the next page for infinite scroll.
type NetworkRow = { id: string }
export type INetworkRailsState = Record<string, ILoadingData<ICrmListResponse<NetworkRow>>>

const EMPTY_RAIL: ILoadingData<ICrmListResponse<NetworkRow>> = {
  isLoading: true,
  isSaving: false,
  isSaved: false,
  data: { data: [], total: 0 },
}

// The relationship entity PersonAffiliationUpdate echoes back, in the shape the
// reducer reads it. `role` arrives as the text[] set — the rail row stays scalar
// (it has to, to sort and substring-filter), so the set is joined to a label
// here. Typing `role` as a bare string was survivable only because `action.data`
// is `any`: the array reached the row and `networkRows.tsx` then called `.trim()`
// on it, which throws. A lone string is still accepted, for any caller sending one.
interface IUpdatedAffiliation {
  id?: string
  role?: string[] | string | null
  isActive?: boolean
  isPrimaryCompanyForPerson?: boolean | null
}

interface INetworkAction {
  type: string
  data?: ICrmListResponse<NetworkRow> & IUpdatedAffiliation
  params?: { railKey?: string; loadMore?: boolean }
}

// Mirror of the rail's server-side pin (NETWORK_RAILS.pinField), applied after an
// in-place patch so a newly starred row leads the list immediately. Without it the
// star moves but the row does not, until a refetch the patch exists to avoid.
const pinPrimaryFirst = (rows: NetworkRow[]): NetworkRow[] => {
  const isPrimary = (row: NetworkRow): boolean =>
    (row as { isPrimaryCompanyForPerson?: boolean | null }).isPrimaryCompanyForPerson === true
  const pinned = rows.filter(isPrimary)
  if (!pinned.length) return rows
  return [...pinned, ...rows.filter((row) => !isPrimary(row))]
}

// Patch an affiliation row in-place after an inline role/status/primary edit, so
// the edit shows without a refetch (which would reset scroll). Recomputes the DM
// badge, and clears the primary star on the person's other rows — the server
// enforces one primary affiliation per person but only returns the row it wrote.
const patchAffiliationRow = (
  state: INetworkRailsState,
  updated: IUpdatedAffiliation,
): INetworkRailsState => {
  if (!updated.id) return state
  const updatedRoles = roleSet(
    typeof updated.role === 'string' ? [updated.role] : (updated.role ?? []),
  )
  const becamePrimary = updated.isPrimaryCompanyForPerson === true
  let changed = false
  const next: INetworkRailsState = {}
  for (const [key, bucket] of Object.entries(state)) {
    const rows = bucket?.data?.data
    if (!key.startsWith('personAffiliations:') || !rows?.some((row) => row.id === updated.id)) {
      next[key] = bucket
      continue
    }
    changed = true
    next[key] = {
      ...bucket,
      data: {
        ...bucket.data,
        data: pinPrimaryFirst(
          rows.map((row) => {
            // Every row in a personAffiliations rail belongs to the same person, so
            // the siblings are exactly the rows the server just cleared.
            if (row.id !== updated.id) {
              return becamePrimary ? { ...row, isPrimaryCompanyForPerson: false } : row
            }
            return {
              ...row,
              role: roleSetToLabel(updatedRoles),
              isActive: updated.isActive ?? (row as { isActive?: boolean }).isActive ?? null,
              isPrimaryCompanyForPerson:
                updated.isPrimaryCompanyForPerson ??
                (row as { isPrimaryCompanyForPerson?: boolean | null }).isPrimaryCompanyForPerson ??
                null,
              // Per-role, not over the joined label: matches
              // isDecisionMakerRole on the server (crm/helpers.ts).
              isDecisionMaker: updatedRoles.some((entry) => /decision maker/i.test(entry)),
            }
          }),
        ),
      },
    }
  }
  return changed ? next : state
}

const networkRails = (
  state: INetworkRailsState = {},
  action: INetworkAction,
): INetworkRailsState => {
  const key = action.params?.railKey
  switch (action.type) {
    case actions.LIST_NETWORK_REQUEST: {
      if (!key) return state
      const prev = state[key] ?? EMPTY_RAIL
      // Keep the current list visible while appending (loadMore); otherwise show loader.
      return { ...state, [key]: { ...prev, isLoading: !action.params?.loadMore } }
    }
    case actions.LIST_NETWORK_SUCCESS: {
      if (!key || !action.data) return state
      const prev = state[key]
      const incoming = action.data
      const data =
        action.params?.loadMore && prev?.data?.data
          ? { data: [...prev.data.data, ...incoming.data], total: incoming.total }
          : { data: incoming.data, total: incoming.total }
      return { ...state, [key]: { isLoading: false, isSaving: false, isSaved: false, data } }
    }
    case actions.LIST_NETWORK_FAILURE: {
      if (!key) return state
      return { ...state, [key]: { ...(state[key] ?? EMPTY_RAIL), isLoading: false } }
    }
    case actions.UPDATE_PERSON_AFFILIATION_SUCCESS:
      return patchAffiliationRow(state, action.data ?? {})
    default:
      return state
  }
}

export default combineReducers({
  loadingArray: loadingArrayReducer(
    [actions.prefix],
    [
      actions.LIST_ORGANIZATIONS_REQUEST,
      actions.LIST_COMPANIES_REQUEST,
      actions.LIST_PEOPLE_REQUEST,
      actions.SHOW_ORGANIZATION_REQUEST,
      actions.SHOW_COMPANY_REQUEST,
      actions.SHOW_PERSON_REQUEST,
      actions.LOAD_PERSON_OVERVIEW_FEED_REQUEST,
      actions.LOAD_COMPANY_OVERVIEW_FEED_REQUEST,
      actions.LOAD_ORGANIZATION_OVERVIEW_FEED_REQUEST,
    ],
  ),
  organizations: createLoadingDataReducer<ICrmListResponse<IOrganizationRow>>([
    actions.LIST_ORGANIZATIONS_REQUEST,
  ]),
  companies: createLoadingDataReducer<ICrmListResponse<ICompanyRow>>([
    actions.LIST_COMPANIES_REQUEST,
  ]),
  companyOptions: createLoadingDataReducer<ICrmListResponse<ICompanyRow>>([
    actions.LIST_COMPANY_OPTIONS_REQUEST,
  ]),
  organizationOptions: createLoadingDataReducer<ICrmListResponse<IOrganizationRow>>([
    actions.LIST_ORGANIZATION_OPTIONS_REQUEST,
  ]),
  people: createLoadingDataReducer<ICrmListResponse<IPersonRow>>([actions.LIST_PEOPLE_REQUEST]),
  // The company page's deals. Writes are SAVING types so the card refetches rather than
  // patching a row whose stage, outcome and open flag are computed server-side.
  deals: createLoadingDataReducer<IDealRow[]>(
    [actions.LIST_DEALS_REQUEST],
    [actions.ADVANCE_DEAL_REQUEST, actions.CLOSE_DEAL_REQUEST, actions.DELETE_DEAL_REQUEST],
  ),
  organization: createLoadingDataReducer<IOrganizationDetail>([actions.SHOW_ORGANIZATION_REQUEST]),
  company: createLoadingDataReducer<ICompanyDetail>([actions.SHOW_COMPANY_REQUEST]),
  person: createLoadingDataReducer<IPersonDetail>([actions.SHOW_PERSON_REQUEST]),
  personOverviewFeed: createLoadingDataReducer<IPersonOverviewFeed>([
    actions.LOAD_PERSON_OVERVIEW_FEED_REQUEST,
  ]),
  companyOverviewFeed: createLoadingDataReducer<IOverviewFeed>([
    actions.LOAD_COMPANY_OVERVIEW_FEED_REQUEST,
  ]),
  organizationOverviewFeed: createLoadingDataReducer<IOrganizationOverviewFeed>([
    actions.LOAD_ORGANIZATION_OVERVIEW_FEED_REQUEST,
  ]),
  // Mention review queue. The three write actions are declared as SAVING types
  // rather than live-edit ones: matching or dismissing a row usually removes it
  // from the current filtered view, so the page refetches instead of patching a
  // row that no longer belongs in the list.
  mentions: createLoadingDataReducer<ICrmListResponse<IMentionRow>>(
    [actions.LIST_MENTIONS_REQUEST],
    [
      actions.RESOLVE_MENTION_ROLE_REQUEST,
      actions.UPDATE_MENTION_STATUS_REQUEST,
      actions.CREATE_MENTION_LEAD_REQUEST,
    ],
  ),
  networkRails,
})
